import {inject, Injectable} from "@angular/core";
import {FarmApiClient} from "src/app/infrastructure/moovement-api/web-api/farm.api-client";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {
  Animal,
  DeviceAssociation,
  Pregnancy,
  SynchronizationStatus,
  WeightMeasurement
} from "src/app/infrastructure/moovement-api/models/asset";
import {AnimalsStore} from "src/app/domain/assets/animals/animals.store";
import {isPresentOrFuture} from "src/app/app.config";
import {Observable, tap} from "rxjs";
import {
  AddWeightMeasurementRequest,
  AnimalApiClient
} from "src/app/infrastructure/moovement-api/web-api/animal.api-client";
import {DataType} from "src/app/domain/domain-models";

@Injectable({
  providedIn: 'root'
})
export class AnimalsService {

  private animalsStore = inject(AnimalsStore);

  constructor(
    private animalApiClient: AnimalApiClient,
    private farmApiClient: FarmApiClient,
    private dataStorageService: DataStorageService
  ) {}

  reset() {
    this.animalsStore.reset();
  }

  loadAnimals(farmId: string, deviceAssociations: DeviceAssociation[]): void {
    this.farmApiClient.getAnimals(farmId).subscribe({
      next: async animals => {
        const activeAnimals = animals.filter(animal => this.isNotDisposed(animal) && this.hasTag(deviceAssociations, animal))
        for (const animal of activeAnimals) {
          await this.persist(animal);
        }
        this.animalsStore.setAllEntities(activeAnimals);
      },
      error: async () => {
        const cachedAnimals = await this.dataStorageService.getAllActiveAssetsMatching<Animal>(DataType.animals, farmId);
        this.animalsStore.setAllEntities(cachedAnimals);
      }
    });
  }

  private async persist(animal: Animal) {
    await this.dataStorageService.saveAsset(DataType.animals, animal.farmId, animal, SynchronizationStatus.upToDate);
  }

  mergeAnimal(animal: Animal): Observable<Animal> {
    return this.animalApiClient.mergeAnimal(animal).pipe(
      tap(animal => this.animalsStore.setEntity(animal)),
      tap(animal => this.persist(animal))
    );
  }

  setProfilePicture(animalId: string, blob: Blob): Observable<Animal> {
    return this.animalApiClient.setProfilePicture(animalId, blob).pipe(
      tap(animal => this.animalsStore.setEntity(animal)),
      tap(animal => this.persist(animal))
    );
  }

  setPregnancyStatus(animalId: string, pregnancyStatus: Pregnancy): Observable<Pregnancy> {
    return this.animalApiClient.updatePregnancyStatus(animalId, pregnancyStatus);
  }

  deleteProfilePicture(animalId: string): Observable<Animal> {
    return this.animalApiClient.deleteProfilePicture(animalId).pipe(
      tap(animal => this.animalsStore.setEntity(animal)),
      tap(animal => this.persist(animal))
    );
  }

  clearAssignedPaddock(animalId: string): Observable<void> {
    return this.animalApiClient.clearAssignedPaddock(animalId);
  }

  addWeightMeasurement(animalId: string, body: AddWeightMeasurementRequest): Observable<WeightMeasurement> {
    return this.animalApiClient.addWeightMeasurement(animalId, body);
  }

  private isNotDisposed(animal: Animal) {
    return animal.disposal?.date === undefined || isPresentOrFuture(new Date(animal.disposal.date));
  }

  private hasTag(deviceAssociations: DeviceAssociation[], animal: Animal) {
    const associations = deviceAssociations.filter(association => association.assetId == animal.id)
    return associations.length > 0 && associations.every(association => association.endDateTime == undefined)
  }
}
